// Fixtures now live in MongoDB, managed via /admin/fixtures — fetch them
// with apiGetFixtures() from lib/storage.js. Every helper below that used to
// read the old hardcoded FIXTURES constant now takes a `fixtures` array
// instead, so callers pass in whatever they fetched from the API.

// Sport emoji mapping
const SPORT_EMOJIS = {
  hockey: "\u{1F3D1}",
  volleyball: "\u{1F3D0}",
  tennis: "\u{1F3BE}",
  "american football": "\u{1F3C8}",
  "muay thai": "\u{1F94A}",
  lacrosse: "\u{1F94D}",
  athletics: "\u{1F3C3}",
  cycling: "\u{1F6B4}",
  swimming: "\u{1F3CA}",
  waterpolo: "\u{1F93D}",
  fencing: "\u{1F93A}",
  kendo: "\u{1F38C}",
  badminton: "\u{1F3F8}",
  netball: "\u{1F3C0}",
  squash: "\u{1F3BE}",
  golf: "\u26F3",
  football: "\u26BD",
  "table tennis": "\u{1F3D3}",
  snowsports: "\u{1F3BF}",
  handball: "\u{1F93E}",
  futsal: "\u26BD",
  basketball: "\u{1F3C0}",
  equestrian: "\u{1F3C7}",
  rowing: "\u{1F6A3}",
  karting: "\u{1F3CE}\uFE0F",
  duathlon: "\u{1F3C3}",
  rugby: "\u{1F3C9}",
  "snooker & pool": "\u{1F3B1}",
  "pole fitness": "\u{1F3AA}",
};

// Parse date/time string to Date object
export function parseEventDateTime(dateStr, timeStr) {
  const months = {
    January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
    July: 6, August: 7, September: 8, October: 9, November: 10, December: 11,
  };
  const [day, monthName, year] = dateStr.split(" ");
  const [h, m] = timeStr.split(":").map(Number);
  return new Date(parseInt(year), months[monthName], parseInt(day), h, m);
}

// Get sport base category (strip suffixes)
export function getSportCategory(sportName) {
  let name = sportName.toLowerCase()
    .replace(/\u2019/g, "'");  // normalize curly apostrophe
  
  const suffixes = [
    "women's medics", "men's medics", "medics",
    "women's", "men's", "mixed"
  ];

  for (const suffix of suffixes) {
    if (name.endsWith(suffix)) {
      name = name.slice(0, -suffix.length).trim();
      break;
    }
  }
  return name;
}

// Get emoji for a sport
export function getSportEmoji(sportName) {
  const category = getSportCategory(sportName);
  return SPORT_EMOJIS[category] || "\u{1F3C6}";
}

// Check if fixture is locked
export function isFixtureLocked(fixture) {
  const eventTime = parseEventDateTime(fixture.date, fixture.time);
  return Date.now() >= eventTime.getTime();
}

// Get countdown string
export function getCountdown(fixture) {
  const eventTime = parseEventDateTime(fixture.date, fixture.time);
  const now = Date.now();
  const diff = eventTime.getTime() - now;

  if (diff <= 0) return "LOCKED";

  const totalMinutes = Math.floor(diff / 60000);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  if (totalHours > 24) {
    const remainHours = totalHours % 24;
    return `${totalDays}d ${remainHours}h`;
  }
  const remainMinutes = totalMinutes % 60;
  return `${totalHours}h ${remainMinutes}m`;
}

// Get unique dates from fixtures, sorted
export function getFixtureDates(fixtures) {
  const dates = [...new Set(fixtures.map(f => f.date))];
  dates.sort((a, b) => {
    const dateA = parseEventDateTime(a, "00:00");
    const dateB = parseEventDateTime(b, "00:00");
    return dateA - dateB;
  });
  return dates;
}

// Format date for display: "20 March" -> "Fri 20 Mar"
export function formatDateShort(dateStr) {
  const dt = parseEventDateTime(dateStr, "12:00");
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${days[dt.getDay()]} ${dt.getDate()} ${months[dt.getMonth()]}`;
}

// Get fixtures for a specific date
export function getFixturesByDate(fixtures, dateStr) {
  return fixtures.filter(f => f.date === dateStr)
    .sort((a, b) => {
      const timeA = parseEventDateTime(a.date, a.time);
      const timeB = parseEventDateTime(b.date, b.time);
      return timeA - timeB;
    });
}

// Get unique sport categories
export function getSportCategories(fixtures) {
  const cats = [...new Set(fixtures.map(f => getSportCategory(f.sport)))];
  return cats.sort();
}
